import React, { useState } from 'react'
import Typography from '@/v2/components/common/Typography'
import Modal from '@/v2/components/common/Modal'
import SelectField from '@/v2/components/common/SelectField'
import Button from '@/v2/components/common/Button'
import { formatCurrency } from '@/v2/utils/format'
import Wrapper from '@/v2/components/common/Wrapper'
import Box from '@mui/material/Box'
import Dialog from '@mui/material/Dialog'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import { usePaymentCalculations } from '@/v2/hooks/usePaymentCalculations'
import {
  useContracts,
  useUpdateContractTreatmentType,
} from '@/v2/hooks/documents/useContracts'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import {
  CONTRACT_TREATMENT_TYPE_LABELS,
  CONTRACT_TREATMENT_TYPE_OPTIONS,
} from '@/v2/constants'

const ContractDetails = ({
  isOpen,
  contract: contractSnapshot,
  patientId,
  onClose,
}) => {
  // The contract prop is a router-state snapshot and goes stale the moment
  // the treatment type changes. The list query is already mounted by the
  // Documents container, so reading the live record from it is free.
  const { contracts } = useContracts(patientId)
  const contract =
    contracts?.find(item => item.id === contractSnapshot?.id) ||
    contractSnapshot

  const { updateTreatmentType } = useUpdateContractTreatmentType(
    patientId,
    contract?.id
  )
  const { onOpenSnackbar } = useSnackbarContext()
  const [selectedTreatmentType, setSelectedTreatmentType] = useState(null)
  const [openSignedConfirmation, setOpenSignedConfirmation] = useState(false)

  const {
    amountWithDiscount,
    totalDue,
    monthlyPayment,
    displayDiscountRate,
    displayInterestRate,
    remainingPaymentCount,
    initialPaymentAmount,
  } = usePaymentCalculations({
    amount: contract?.totalCost,
    initialPayment: contract?.downPayment || 0,
    installments: contract?.monthlyPaymentCount || 1,
    discountRate: contract?.discountRate || 0,
    interestRate: contract?.interestRate || 0,
    classification:
      contract?.monthlyPaymentCount === 1 ? 'one_time_payment' : 'installments',
  })

  const isSinglePayment =
    initialPaymentAmount === 0 && contract?.monthlyPaymentCount === 1

  const treatmentType = selectedTreatmentType ?? contract?.treatmentType
  const treatmentTypeChanged = treatmentType !== contract?.treatmentType

  const handleChangeClick = () => {
    if (contract.signed) {
      setOpenSignedConfirmation(true)
    } else {
      handleChangeTreatmentType()
    }
  }

  const handleChangeTreatmentType = async () => {
    try {
      const { error } = await updateTreatmentType(treatmentType)

      if (!error) {
        setSelectedTreatmentType(null)
        onOpenSnackbar('Treatment type updated', 'success')
      }
    } catch (error) {
      onOpenSnackbar('Something went wrong.', 'error')
    } finally {
      setOpenSignedConfirmation(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      title="Contract Details"
      confirmLabel="Close"
      showClose={false}
      onConfirm={onClose}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
    >
      <Box
        bgcolor={'gray.light'}
        display={'flex'}
        flexDirection={'column'}
        borderRadius={2}
        px={2}
        py={2}
      >
        <Wrapper>
          <Box mb={1}>
            <Typography variant="body2">CID#</Typography>
            <Typography variant="body2" bold>
              {contract.identifierToken}
            </Typography>
          </Box>
          <Box mb={1}>
            <Typography variant="body2">Status</Typography>
            <Typography variant="body2" bold>
              {contract.signed ? 'Signed' : 'Out for sign'}
            </Typography>
          </Box>
          <Box mb={1}>
            <Typography variant="body2">Treatment</Typography>
            <Typography variant="body2" bold>
              {CONTRACT_TREATMENT_TYPE_LABELS[contract.treatmentType]}
            </Typography>
          </Box>
        </Wrapper>

        {/* Archived contracts have no PDF, so there is nothing to re-render. */}
        {contract.active && (
          <Box display="flex" flexDirection="column" gap={0.5} mb={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <SelectField
                fullWidth
                noMargin
                size="small"
                name="treatmentType"
                inputProps={{ 'aria-label': 'Treatment type' }}
                options={CONTRACT_TREATMENT_TYPE_OPTIONS}
                value={treatmentType}
                onChange={setSelectedTreatmentType}
              />
              <Button
                variant="contained"
                color="secondary"
                size="small"
                disabled={!treatmentTypeChanged}
                onClick={handleChangeClick}
                sx={{ flexShrink: 0 }}
              >
                Change
              </Button>
            </Box>
            {contract.signed && (
              <Typography variant="warning">
                This contract is signed. Changing the treatment re-renders the
                PDF on its next download.
              </Typography>
            )}
          </Box>
        )}

        <Divider sx={{ my: 1.5 }} />

        <Wrapper>
          {isSinglePayment ? (
            <Box mb={1}>
              <Typography variant="body2">Payment Type</Typography>
              <Typography variant="body2" bold>
                Single
              </Typography>
            </Box>
          ) : (
            <>
              {initialPaymentAmount > 0 && (
                <Box mb={1}>
                  <Typography variant="body2">Initial Payment</Typography>
                  <Typography variant="body2" bold>
                    {formatCurrency(
                      parseFloat(initialPaymentAmount).toFixed(2)
                    )}
                  </Typography>
                </Box>
              )}

              {contract?.monthlyPaymentCount > 1 && (
                <Box mb={1}>
                  <Typography variant="body2">Monthly Payments</Typography>
                  <Typography variant="body2" bold>
                    {remainingPaymentCount} x{' '}
                    {formatCurrency(parseFloat(monthlyPayment).toFixed(2))}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Wrapper>

        <Divider sx={{ my: 1.5 }} />

        <Wrapper>
          <Box>
            <Typography variant="body2">Total Cost</Typography>
            <Typography variant="body2" bold>
              {formatCurrency(parseFloat(contract?.totalCost).toFixed(2))}
            </Typography>
          </Box>

          {displayDiscountRate > 0 && (
            <>
              <Box>
                <Typography variant="body2">Discount</Typography>
                <Typography variant="body2" bold>
                  {displayDiscountRate}%
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2">
                  Total with {displayDiscountRate}% discount
                </Typography>
                <Typography variant="body2" bold>
                  {formatCurrency(parseFloat(amountWithDiscount).toFixed(2))}
                </Typography>
              </Box>
            </>
          )}

          {displayInterestRate > 0 && (
            <>
              <Box>
                <Typography variant="body2">Interest Rate</Typography>
                <Typography variant="body2" bold>
                  {displayInterestRate}%
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2">
                  Total with {displayInterestRate}% interest
                </Typography>
                <Typography variant="body2" bold>
                  {formatCurrency(parseFloat(totalDue).toFixed(2))}
                </Typography>
              </Box>
            </>
          )}
        </Wrapper>
      </Box>

      <Dialog
        open={openSignedConfirmation}
        onClose={() => setOpenSignedConfirmation(false)}
        maxWidth="sm"
        fullWidth
      >
        <div style={{ padding: '38px 30px 23px 30px', textAlign: 'center' }}>
          <Typography variant="h3" sx={{ marginBottom: '24px' }}>
            Change treatment type on a signed contract?
          </Typography>
          <Typography
            variant="body2"
            sx={{ marginBottom: '24px', color: 'text.secondary' }}
          >
            The signed PDF will be re-rendered with the new wording on its next
            download. The copy emailed to the patient at signing keeps the old
            wording.
          </Typography>
          <Grid
            display="flex"
            width={'100%'}
            justifyContent={'flex-end'}
            gap={1}
          >
            <Button
              variant="text"
              color="secondary"
              onClick={() => setOpenSignedConfirmation(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleChangeTreatmentType}
            >
              Yes, change
            </Button>
          </Grid>
        </div>
      </Dialog>
    </Modal>
  )
}

export default ContractDetails
